<template>
  <div v-click-outside="close" class="bell">
    <button type="button" class="bell-btn" :title="unread ? (unread === 1 ? '1 notificação nova' : `${unread} notificações novas`) : 'Notificações'" @click="toggle">
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" /><path d="M13.73 21a2 2 0 0 1-3.46 0" /></svg>
      <span v-if="unread" class="count">{{ unread > 9 ? "9+" : unread }}</span>
    </button>
    <div v-if="open" class="panel">
      <div class="panel-head">
        <strong>Notificações</strong>
        <button v-if="unread" type="button" class="link" @click="readAll">Marcar todas como lidas</button>
      </div>
      <p v-if="!items.length" class="empty">Nada por aqui ainda. Avisamos quando um {{ $patient }} agendar, remarcar ou cancelar.</p>
      <ul v-else>
        <ComponentNotificationItem v-for="n in items" :key="n.id" :n="n" @open="openItem" @read="markRead" @link="onLink" @action="onAction" />
      </ul>
      <router-link v-if="items.length" :to="{ name: 'Notifications' }" class="see-all" @click="open = false">Ver todas as notificações</router-link>
      <label class="digest">
        <input type="checkbox" :checked="digest" @change="setDigest($event.target.checked)" />
        Receber resumo por e-mail do que eu não ler
      </label>
      <label class="digest digest-next">
        <input type="checkbox" :checked="routine" @change="setRoutine($event.target.checked)" />
        Agenda do dia e resumo da semana por e-mail
      </label>
    </div>
    <teleport to="body">
      <div v-if="message" class="team-overlay" @click.self="closeMessage">
        <div class="team-card" role="dialog" aria-modal="true" aria-labelledby="team-title">
          <p class="team-from">💙 Mensagem do time Ordexa</p>
          <h2 id="team-title">{{ message.title }}</h2>
          <p v-if="message.body" class="team-body"><ComponentRichText :text="message.body" @link="(u) => onLink(message, u, 'modal')" /></p>
          <div class="team-actions">
            <button v-if="modalActions[1]" type="button" class="team-ghost" @click="runAction(modalActions[1], 'secondary')">{{ modalActions[1].label }}</button>
            <button v-if="modalActions[0]" type="button" class="team-btn" @click="runAction(modalActions[0], 'primary')">{{ modalActions[0].label }}</button>
            <button type="button" :class="modalActions.length ? 'team-link' : 'team-btn'" @click="closeMessage">{{ modalActions.length ? "Depois" : "Obrigado!" }}</button>
          </div>
        </div>
      </div>
    </teleport>
  </div>
</template>

<script>
import axios from "axios";
import Fetch from "@/fetch/index";
import { markModalShown, modalShownThisLoad } from "@/utils/nudges";
import { track } from "@/utils/analytics";
import ComponentNotificationItem from "@/components/ComponentNotificationItem.vue";
import ComponentRichText from "@/components/ComponentRichText.vue";
import { cleanActions } from "@/utils/richText";
import { markReadRemote, openUrl, trackClick } from "@/utils/notifications";

// Personal messages from the team (a suggestion that went live, a note from us) open by themselves once.
// Left unread, they go in the daily digest e-mail like any other notice.
const TEAM_KINDS = ["suggestion_done", "team_message"];

const POLL_MS = 60000;

export default {
  name: "ComponentNotificationsBell",
  components: { ComponentNotificationItem, ComponentRichText },
  data() {
    return { open: false, items: [], unread: 0, digest: true, routine: true, timer: null, baseTitle: document.title, message: null };
  },
  mounted() {
    window.addEventListener("ordexa:notifications-changed", this.load);
    this.load();
    this.timer = setInterval(() => {
      if (document.visibilityState === "visible") this.load();
    }, POLL_MS);
  },
  unmounted() {
    window.removeEventListener("ordexa:notifications-changed", this.load);
    clearInterval(this.timer);
  },
  computed: {
    // Modal buttons: the first action is the main one; without actions, the notification's own link ("Ver agora").
    modalActions() {
      if (!this.message) return [];
      const acts = cleanActions(this.message.actions);
      if (acts.length) return acts;
      return this.message.url ? [{ label: "Ver agora", url: this.message.url }] : [];
    },
  },
  methods: {
    async load() {
      try {
        const { data } = await Fetch.get("/api/v1/notifications");
        this.items = data.notifications || [];
        this.unread = data.unread_count || 0;
        this.digest = data.digest_email !== false;
        this.routine = data.routine_email !== false;
        const clean = document.title.replace(/^\(\d+\+?\) /, "");
        document.title = this.unread ? `(${this.unread}) ${clean}` : clean;
        this.showTeamMessage();
      } catch (e) {
        // the bell must never break the app
      }
    },
    showTeamMessage() {
      if (this.message || modalShownThisLoad()) return;
      const msg = this.items.find((n) => !n.read && TEAM_KINDS.includes(n.kind));
      if (!msg) return;
      markModalShown();
      this.message = msg;
      track("team_message_shown", { kind: msg.kind });
    },
    closeMessage() {
      if (this.message) this.markRead(this.message);
      this.message = null;
    },
    runAction(action, which) {
      const msg = this.message;
      this.message = null;
      this.finishClick(msg, action.url, which, "modal");
    },
    toggle() {
      this.open = !this.open;
      if (this.open) this.load();
    },
    close() {
      this.open = false;
    },
    async readAll() {
      await axios.post("/api/v1/notifications/read_all", { meta: { contentType: "application/json" } }).catch(() => {});
      this.items = this.items.map((n) => ({ ...n, read: true }));
      this.unread = 0;
      document.title = document.title.replace(/^\(\d+\+?\) /, "");
    },
    markRead(n) {
      if (n.read) return;
      markReadRemote(n.id);
      n.read = true;
      this.unread = Math.max(0, this.unread - 1);
      const clean = document.title.replace(/^\(\d+\+?\) /, "");
      document.title = this.unread ? `(${this.unread}) ${clean}` : clean;
    },
    setDigest(value) {
      this.digest = value;
      axios.patch("/api/v1/notifications/preferences", { digest_email: value, meta: { contentType: "application/json" } }).catch(() => {});
    },
    setRoutine(value) {
      this.routine = value;
      axios.patch("/api/v1/notifications/preferences", { routine_email: value, meta: { contentType: "application/json" } }).catch(() => {});
    },
    // Any click on a notification (item, link or button) marks it read and is tracked.
    finishClick(n, url, which, where) {
      if (!n.read) this.markRead(n);
      if (this.message && this.message.id === n.id) this.message = null;
      trackClick(n, which, where);
      this.open = false;
      if (url) openUrl(this.$router, url);
    },
    openItem(n) {
      this.finishClick(n, n.url, "item", "bell");
    },
    onLink(n, url, where = "bell") {
      this.finishClick(n, url, "link", where);
    },
    onAction(n, action, which) {
      this.finishClick(n, action.url, which, "bell");
    },
  },
};
</script>

<style scoped>
.bell { position: relative; margin-right: 0.8em; }
/* 44px touch target without changing the header layout */
.bell-btn::after { content: ""; position: absolute; inset: -2px -5px; }
.bell-btn { position: relative; background: none; border: none; color: #555; cursor: pointer; padding: 6px; display: flex; align-items: center; }
.count { position: absolute; top: 0; right: -2px; background: #d73641; color: #fff; font-size: 10px; font-weight: 700; border-radius: 999px; min-width: 16px; height: 16px; display: flex; align-items: center; justify-content: center; padding: 0 3px; }
.panel { position: absolute; right: 0; top: 40px; width: 380px; max-width: calc(100vw - 24px); max-height: 75vh; display: flex; flex-direction: column; overflow: hidden; background: #fff; border-radius: 12px; box-shadow: 0 12px 40px rgba(0, 0, 0, 0.18); z-index: 1200; }
@media (max-width: 600px) { .panel { position: fixed; left: 12px; right: 12px; top: 64px; width: auto; max-width: none; } }
.digest.digest-next { border-top: none; padding-top: 0; }
.digest { display: flex; gap: 8px; align-items: center; padding: 10px 14px; border-top: 1px solid #eef1f3; font-size: 0.82em; color: #5f6f75; cursor: pointer; }
.panel-head { display: flex; justify-content: space-between; align-items: center; padding: 0.8em 1em; border-bottom: 1px solid #eef1f3; }
.link { background: none; border: none; color: #1a8ea8; font-weight: 600; cursor: pointer; font-size: 0.85em; }
.empty { padding: 1.2em 1em; color: #888; font-size: 0.9em; margin: 0; }
ul { list-style: none; margin: 0; padding: 0; overflow-y: auto; flex: 1 1 auto; min-height: 0; }
.panel-head, .see-all, .digest { flex: none; }
.team-overlay { position: fixed; inset: 0; background: rgba(20, 30, 40, 0.45); display: flex; align-items: center; justify-content: center; padding: 16px; z-index: 3000; }
.team-card { background: #fff; border-radius: 16px; max-width: 420px; width: 100%; padding: 26px 24px 20px; box-shadow: 0 20px 60px rgba(0, 0, 0, 0.25); }
.team-from { margin: 0 0 8px; font-size: 0.85em; font-weight: 700; color: #1a8ea8; }
.team-card h2 { margin: 0 0 10px; font-size: 1.3em; line-height: 1.3; color: #1f2d3d; }
.team-body { margin: 0 0 20px; color: #4a5560; line-height: 1.55; white-space: pre-line; }
.team-actions { display: flex; gap: 12px; align-items: center; justify-content: flex-end; }
.team-btn { background: #29b1cc; color: #fff; border: 0; border-radius: 10px; padding: 12px 20px; font-weight: 700; font-size: 1em; cursor: pointer; }
.team-link { background: none; border: 0; color: #5f6f75; cursor: pointer; font-size: 0.95em; }
.see-all { display: block; text-align: center; padding: 11px 14px; font-weight: 700; font-size: 0.85em; color: #1a8ea8; border-top: 1px solid #eef1f3; text-decoration: none; }
.see-all:hover { background: #f7fbfc; }
.team-ghost { background: #fff; color: #1a8ea8; border: 1px solid #29b1cc; border-radius: 10px; padding: 11px 18px; font-weight: 700; font-size: 1em; cursor: pointer; }
.team-actions { flex-wrap: wrap; }

@media (max-width: 600px) {
  .team-card { padding: 22px 18px 16px; max-height: calc(100vh - 32px); overflow-y: auto; }
  .team-actions { flex-direction: column; align-items: stretch; }
  .team-btn { order: 1; } .team-ghost { order: 2; } .team-link { order: 3; }
  .team-btn, .team-ghost { width: 100%; }
  .team-link { padding: 8px; }
}
</style>
